import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUpload, FaTrash, FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaImage, FaPlus, FaMinus } from 'react-icons/fa';
import Layout from './Layout';
import SdProductCampaignAPI from '../services/SdProductCampaignAPI';
import '../assets/css/SdProductCampaigns.css';

const SdProductCampaigns = () => {
    // Estados para archivos
    const [ownProductsFile, setOwnProductsFile] = useState(null);
    const [competitorFile, setCompetitorFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    // Estados para productos procesados
    const [ownProducts, setOwnProducts] = useState([]);
    const [competitorProducts, setCompetitorProducts] = useState([]);
    const [selectedOwnProducts, setSelectedOwnProducts] = useState([]);
    const [selectedCompetitorProducts, setSelectedCompetitorProducts] = useState([]);

    // Configuration states
    const [campaignName, setCampaignName] = useState('');
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(10);
    const [defaultBid, setDefaultBid] = useState(1.0);
    const [tactic, setTactic] = useState('T00030');
    const [bidOptimization, setBidOptimization] = useState('clicks');
    const [createIndividualCampaigns, setCreateIndividualCampaigns] = useState(false);

    // Estados de UI
    const [loading, setLoading] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1); // 1: Upload, 2: Select, 3: Configure, 4: Create

    // Obtener profileId del localStorage
    const getProfileId = () => {
        // El Header guarda el profileId en 'selectedAccountValue'
        const profileId = localStorage.getItem('selectedAccountValue');
        if (profileId) {
            return profileId;
        }
        return null;
    };

    // Manejar carga de archivo CSV de productos propios
    const handleOwnProductsChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setOwnProductsFile(file);
        }
    };

    // Manejar carga de archivo CSV de competidores
    const handleCompetitorChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setCompetitorFile(file);
        }
    };

    // Manejar carga de logo
    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Procesar archivos CSV
    const handleUploadFiles = async () => {
        if (!ownProductsFile && !competitorFile) {
            setError('You must upload at least one CSV file');
            return;
        }

        setUploadingFiles(true);
        setError(null);

        try {
            const response = await SdProductCampaignAPI.uploadFiles(ownProductsFile, competitorFile, logoFile);

            if (response.success) {
                setOwnProducts(response.data.ownProducts || []);
                setCompetitorProducts(response.data.competitorProducts || []);
                setSelectedOwnProducts(response.data.ownProducts.map(p => p.asin) || []);
                setSelectedCompetitorProducts(response.data.competitorProducts.map(p => p.asin) || []);
                setStep(2);
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error uploading files');
        } finally {
            setUploadingFiles(false);
        }
    };

    // Toggle own product selection
    const toggleOwnProduct = (asin) => {
        setSelectedOwnProducts(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    // Toggle competitor product selection
    const toggleCompetitorProduct = (asin) => {
        setSelectedCompetitorProducts(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    // Select todos los productos propios
    const selectAllOwnProducts = () => {
        setSelectedOwnProducts(ownProducts.map(p => p.asin));
    };

    // Deselectr todos los productos propios
    const deselectAllOwnProducts = () => {
        setSelectedOwnProducts([]);
    };

    // Select todos los competidores
    const selectAllCompetitors = () => {
        setSelectedCompetitorProducts(competitorProducts.map(p => p.asin));
    };

    // Deselectr todos los competidores
    const deselectAllCompetitors = () => {
        setSelectedCompetitorProducts([]);
    };

    // Create campaigns
    const handleCreateCampaigns = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('No account selected. Please select an account in the header.');
            return;
        }

        if (selectedOwnProducts.length === 0) {
            setError('You must select at least one own product');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const today = new Date();
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() + 30);

            const startDateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
            const endDateStr = endDate.toISOString().slice(0, 10).replace(/-/g, '');

            const response = await SdProductCampaignAPI.createCampaign({
                profileId: profileId,
                campaignName: campaignName || `SD_Product_${Date.now()}`,
                dailyBudget: dailyBudget,
                headline: headline || 'Discover our products!',
                ownProducts: selectedOwnProducts,
                competitorProducts: selectedCompetitorProducts,
                startDate: startDateStr,
                endDate: endDateStr,
                tactic: tactic,
                bidOptimization: bidOptimization,
                defaultBid: defaultBid,
                state: 'paused',
                logo: logoFile
            });

            setResult(response);
            setStep(4);
        } catch (err) {
            setError(err.message || 'Error creating campaigns');
        } finally {
            setLoading(false);
        }
    };

    // Reset process
    const handleReset = () => {
        setOwnProductsFile(null);
        setCompetitorFile(null);
        setLogoFile(null);
        setLogoPreview(null);
        setOwnProducts([]);
        setCompetitorProducts([]);
        setSelectedOwnProducts([]);
        setSelectedCompetitorProducts([]);
        setCampaignName('');
        setHeadline('');
        setDailyBudget(10);
        setDefaultBid(1.0);
        setResult(null);
        setError(null);
        setStep(1);
    };

    return (
        <Layout>
            <Container fluid className="sd-product-campaigns-page py-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className="text-white mb-3">
                            <FaRocket className="me-2" />
                            Sponsored Display - Product Campaigns
                        </h2>
                        <p className="text-muted">
                            Create SD product campaigns with competitor targeting, creatives, and logos
                        </p>
                    </Col>
                </Row>

                {/* Progress Steps */}
                <Row className="mb-4">
                    <Col>
                        <div className="progress-steps">
                            <div className={`step ${step >= 1 ? 'active' : ''}`}>
                                <span className="step-number">1</span>
                                <span className="step-label">Upload files</span>
                            </div>
                            <div className={`step ${step >= 2 ? 'active' : ''}`}>
                                <span className="step-number">2</span>
                                <span className="step-label">Select products</span>
                            </div>
                            <div className={`step ${step >= 3 ? 'active' : ''}`}>
                                <span className="step-number">3</span>
                                <span className="step-label">Configure</span>
                            </div>
                            <div className={`step ${step >= 4 ? 'active' : ''}`}>
                                <span className="step-number">4</span>
                                <span className="step-label">Resultado</span>
                            </div>
                        </div>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Step 1: Upload Files */}
                {step === 1 && (
                    <Row>
                        <Col md={4}>
                            <Card className="upload-card">
                                <Card.Body>
                                    <Card.Title><FaFileAlt className="me-2" />Own Products (CSV)</Card.Title>
                                    <Card.Text className="text-muted small">
                                        Upload a CSV with your product ASINs. Columns: ASIN, SKU, Price (optional)
                                    </Card.Text>
                                    <Form.Group>
                                        <Form.Label className="upload-area w-100">
                                            <FaUpload size={30} className="mb-2" />
                                            <br />
                                            {ownProductsFile ? ownProductsFile.name : 'Click to upload CSV'}
                                            <Form.Control
                                                type="file"
                                                accept=".csv"
                                                onChange={handleOwnProductsChange}
                                                hidden
                                            />
                                        </Form.Label>
                                    </Form.Group>
                                    {ownProductsFile && (
                                        <Button variant="outline-danger" size="sm" onClick={() => setOwnProductsFile(null)}>
                                            <FaTrash /> Delete
                                        </Button>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="upload-card">
                                <Card.Body>
                                    <Card.Title><FaFileAlt className="me-2" />Competitor Products (CSV)</Card.Title>
                                    <Card.Text className="text-muted small">
                                        Upload a CSV with competitor ASINs that you want to target
                                    </Card.Text>
                                    <Form.Group>
                                        <Form.Label className="upload-area w-100">
                                            <FaUpload size={30} className="mb-2" />
                                            <br />
                                            {competitorFile ? competitorFile.name : 'Click to upload CSV'}
                                            <Form.Control
                                                type="file"
                                                accept=".csv"
                                                onChange={handleCompetitorChange}
                                                hidden
                                            />
                                        </Form.Label>
                                    </Form.Group>
                                    {competitorFile && (
                                        <Button variant="outline-danger" size="sm" onClick={() => setCompetitorFile(null)}>
                                            <FaTrash /> Delete
                                        </Button>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="upload-card">
                                <Card.Body>
                                    <Card.Title><FaImage className="me-2" />Logo/Image (Optional)</Card.Title>
                                    <Card.Text className="text-muted small">
                                        Upload a logo to use in the SD creative. PNG or JPG, maximum 600x100px
                                    </Card.Text>
                                    <Form.Group>
                                        <Form.Label className="upload-area w-100">
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '60px', maxWidth: '100%' }} />
                                            ) : (
                                                <>
                                                    <FaUpload size={30} className="mb-2" />
                                                    <br />
                                                    Click to upload image
                                                </>
                                            )}
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoChange}
                                                hidden
                                            />
                                        </Form.Label>
                                    </Form.Group>
                                    {logoFile && (
                                        <Button variant="outline-danger" size="sm" onClick={() => { setLogoFile(null); setLogoPreview(null); }}>
                                            <FaTrash /> Delete
                                        </Button>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} className="mt-4 text-center">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleUploadFiles}
                                disabled={uploadingFiles || (!ownProductsFile && !competitorFile)}
                            >
                                {uploadingFiles ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Processing files...
                                    </>
                                ) : (
                                    <>
                                        <FaUpload className="me-2" />
                                        Process Files
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                )}

                {/* Step 2: Select Products */}
                {step === 2 && (
                    <Row>
                        <Col md={6}>
                            <Card className="product-card">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong>My Products</strong>
                                        <Badge bg="primary" className="ms-2">{selectedOwnProducts.length}/{ownProducts.length}</Badge>
                                    </span>
                                    <div>
                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={selectAllOwnProducts}>
                                            <FaPlus /> All
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={deselectAllOwnProducts}>
                                            <FaMinus /> None
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="product-list">
                                    {ownProducts.length === 0 ? (
                                        <p className="text-muted text-center">No own products</p>
                                    ) : (
                                        <Table hover responsive size="sm">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>ASIN</th>
                                                    <th>SKU</th>
                                                    <th>Precio</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ownProducts.map((product, idx) => (
                                                    <tr
                                                        key={idx}
                                                        className={selectedOwnProducts.includes(product.asin) ? 'table-primary' : ''}
                                                        onClick={() => toggleOwnProduct(product.asin)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>
                                                            <Form.Check
                                                                checked={selectedOwnProducts.includes(product.asin)}
                                                                onChange={() => { }}
                                                            />
                                                        </td>
                                                        <td>{product.asin}</td>
                                                        <td>{product.sku || '-'}</td>
                                                        <td>{product.price ? `$${product.price}` : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="product-card">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong>Competitors (Targeting)</strong>
                                        <Badge bg="warning" className="ms-2">{selectedCompetitorProducts.length}/{competitorProducts.length}</Badge>
                                    </span>
                                    <div>
                                        <Button variant="outline-warning" size="sm" className="me-2" onClick={selectAllCompetitors}>
                                            <FaPlus /> All
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={deselectAllCompetitors}>
                                            <FaMinus /> None
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="product-list">
                                    {competitorProducts.length === 0 ? (
                                        <p className="text-muted text-center">No competitor products</p>
                                    ) : (
                                        <Table hover responsive size="sm">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>ASIN</th>
                                                    <th>Rating</th>
                                                    <th>Reviews</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {competitorProducts.map((product, idx) => (
                                                    <tr
                                                        key={idx}
                                                        className={selectedCompetitorProducts.includes(product.asin) ? 'table-warning' : ''}
                                                        onClick={() => toggleCompetitorProduct(product.asin)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>
                                                            <Form.Check
                                                                checked={selectedCompetitorProducts.includes(product.asin)}
                                                                onChange={() => { }}
                                                            />
                                                        </td>
                                                        <td>{product.asin}</td>
                                                        <td>{product.rating || '-'}</td>
                                                        <td>{product.reviews || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} className="mt-4">
                            <div className="d-flex justify-content-between">
                                <Button variant="secondary" onClick={() => setStep(1)}>
                                    Back
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => setStep(3)}
                                    disabled={selectedOwnProducts.length === 0}
                                >
                                    Continue
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Step 3: Configure Campaign */}
                {step === 3 && (
                    <Row>
                        <Col md={8} className="mx-auto">
                            <Card className="config-card">
                                <Card.Header>
                                    <strong>Campaign Configuration</strong>
                                </Card.Header>
                                <Card.Body>
                                    <Form>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Campaign Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={campaignName}
                                                        onChange={(e) => setCampaignName(e.target.value)}
                                                        placeholder="SD_Product_MiProducto"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Headline (Ad Title)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={headline}
                                                        onChange={(e) => setHeadline(e.target.value)}
                                                        placeholder="Discover our products!"
                                                        maxLength={50}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {headline.length}/50 characters
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Daily Budget ($)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={dailyBudget}
                                                        onChange={(e) => setDailyBudget(parseFloat(e.target.value))}
                                                        min={1}
                                                        step={0.01}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Default Bid ($)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={defaultBid}
                                                        onChange={(e) => setDefaultBid(parseFloat(e.target.value))}
                                                        min={0.02}
                                                        step={0.01}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Tactic</Form.Label>
                                                    <Form.Select
                                                        value={tactic}
                                                        onChange={(e) => setTactic(e.target.value)}
                                                    >
                                                        <option value="T00030">Product Targeting (Contextual)</option>
                                                        <option value="T00020">Remarketing (Audiences)</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Bid Optimization</Form.Label>
                                                    <Form.Select
                                                        value={bidOptimization}
                                                        onChange={(e) => setBidOptimization(e.target.value)}
                                                    >
                                                        <option value="clicks">Clicks</option>
                                                        <option value="conversions">Conversions</option>
                                                        <option value="reach">Reach</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3 pt-4">
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Create one campaign per product"
                                                        checked={createIndividualCampaigns}
                                                        onChange={(e) => setCreateIndividualCampaigns(e.target.checked)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <hr />

                                        <Row>
                                            <Col>
                                                <h6>Summary:</h6>
                                                <ul className="summary-list">
                                                    <li>
                                                        <Badge bg="primary" className="me-2">{selectedOwnProducts.length}</Badge>
                                                        Selected own products
                                                    </li>
                                                    <li>
                                                        <Badge bg="warning" className="me-2">{selectedCompetitorProducts.length}</Badge>
                                                        Competitors for targeting
                                                    </li>
                                                    <li>
                                                        {logoFile ? (
                                                            <Badge bg="success" className="me-2">OK</Badge>
                                                        ) : (
                                                            <Badge bg="secondary" className="me-2">-</Badge>
                                                        )}
                                                        Logo {logoFile ? 'included' : 'not included'}
                                                    </li>
                                                </ul>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>

                            <div className="d-flex justify-content-between mt-4">
                                <Button variant="secondary" onClick={() => setStep(2)}>
                                    Back
                                </Button>
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={handleCreateCampaigns}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Creating campaigns...
                                        </>
                                    ) : (
                                        <>
                                            <FaRocket className="me-2" />
                                            Generate Campaigns
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Step 4: Result */}
                {step === 4 && result && (
                    <Row>
                        <Col md={8} className="mx-auto">
                            <Card className={`result-card ${result.success ? 'success' : 'error'}`}>
                                <Card.Header>
                                    {result.success ? (
                                        <><FaCheckCircle className="text-success me-2" /> Campaigns Created Successfully</>
                                    ) : (
                                        <><FaExclamationTriangle className="text-warning me-2" /> Campaigns Created with Errors</>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {result.data && (
                                        <div className="result-details">
                                            {result.data.campaign && (
                                                <div className="result-item">
                                                    <strong>Campaign:</strong>
                                                    <Badge bg="success" className="ms-2">
                                                        ID: {result.data.campaign.campaignId}
                                                    </Badge>
                                                </div>
                                            )}
                                            {result.data.adGroup && (
                                                <div className="result-item">
                                                    <strong>Ad Group:</strong>
                                                    <Badge bg="info" className="ms-2">
                                                        ID: {result.data.adGroup.adGroupId}
                                                    </Badge>
                                                </div>
                                            )}
                                            {result.data.productAds && (
                                                <div className="result-item">
                                                    <strong>Product Ads:</strong>
                                                    <Badge bg="primary" className="ms-2">
                                                        {Array.isArray(result.data.productAds) ? result.data.productAds.length : 1} created
                                                    </Badge>
                                                </div>
                                            )}
                                            {result.data.targeting && (
                                                <div className="result-item">
                                                    <strong>Targeting:</strong>
                                                    <Badge bg="warning" className="ms-2">
                                                        {Array.isArray(result.data.targeting) ? result.data.targeting.length : 1} targets
                                                    </Badge>
                                                </div>
                                            )}
                                            {result.data.creative && (
                                                <div className="result-item">
                                                    <strong>Creative:</strong>
                                                    <Badge bg="secondary" className="ms-2">
                                                        Created with logo and headline
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {result.errors && result.errors.length > 0 && (
                                        <Alert variant="warning" className="mt-3">
                                            <strong>Warnings:</strong>
                                            <ul className="mb-0">
                                                {result.errors.map((err, idx) => (
                                                    <li key={idx}>{err}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>

                            <div className="text-center mt-4">
                                <Button variant="primary" size="lg" onClick={handleReset}>
                                    <FaPlus className="me-2" />
                                    Create New Campaign
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
        </Layout>
    );
};

export default SdProductCampaigns;

